import Tooltip from '@mui/material/Tooltip'
import { format, isValid } from 'date-fns'
import { DateTime } from 'luxon'
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCommonAndCompanyEvents } from '../../hooks/useCommonAndCompanyEvents.js'
import DateFieldInput from './DateFieldInput.jsx'
import { DatePickerWrapper as StyledDatePickerWrapper } from './datepickerwrapper.jsx'
import { localeMap, toDate, toISO } from './utils'
import 'react-datepicker/dist/react-datepicker.css'

const toIsoDateKey = (date) => format(date, 'yyyy-MM-dd')

/**
 * A masked date picker with custom header, year dropdown, and holiday highlighting.
 * Uses react-datepicker with DateFieldInput for automatic digit masking and validation based on user locale
 */
const DatePickerMasked = forwardRef(
	({ id, value, onChange, name, label, required, disabled, error, minDate, maxDate, ...rest }, ref) => {
		const { t, i18n } = useTranslation()
		const lang = (i18n.resolvedLanguage ?? 'fi').split('-')[0]
		const localeConfig = localeMap[lang] ?? localeMap.fi
		const [yearMenuOpen, setYearMenuOpen] = useState(false)
		const yearMenuRef = useRef(null)

		useEffect(() => {
			if (!yearMenuOpen) return

			const handlePointerDown = (event) => {
				if (!yearMenuRef.current?.contains(event.target)) {
					setYearMenuOpen(false)
				}
			}

			const handleKeyDown = (event) => {
				if (event.key === 'Escape') setYearMenuOpen(false)
			}

			document.addEventListener('mousedown', handlePointerDown)
			document.addEventListener('keydown', handleKeyDown)

			return () => {
				document.removeEventListener('mousedown', handlePointerDown)
				document.removeEventListener('keydown', handleKeyDown)
			}
		}, [yearMenuOpen])

		useEffect(() => {
			if (!yearMenuOpen) return
			const selectedYear = yearMenuRef.current?.querySelector('.dp-year-option--selected')
			selectedYear?.scrollIntoView({ block: 'nearest' })
		}, [yearMenuOpen])

		const start = useMemo(() => DateTime.now().minus({ years: 1 }).startOf('year').toISODate(), [])
		const end = useMemo(() => DateTime.now().plus({ years: 2 }).endOf('year').toISODate(), [])

		// Fetch holidays for the calendar range to enable highlighting and tooltips
		const { data: holidays = [] } = useCommonAndCompanyEvents({ start, end })

		const holidayByIso = useMemo(() => {
			const map = new Map()
			for (const holiday of holidays) {
				if (holiday?.date) map.set(holiday.date, holiday)
			}
			return map
		}, [holidays])

		const holidayTooltipByIso = useMemo(() => {
			const map = new Map()
			for (const holiday of holidays) {
				if (!holiday?.date || !holiday?.descKey) continue
				map.set(holiday.date, t(holiday.descKey))
			}
			return map
		}, [holidays, t])

		const handleDateChange = useCallback(
			(date) => {
				setYearMenuOpen(false)
				if (!date) {
					onChange?.(null)
					return
				}

				if (isValid(date)) {
					onChange?.(toISO(date))
				}
			},
			[onChange],
		)

		/** Custom header with month navigation and year dropdown */
		const renderCustomHeader = useCallback(
			({ date, decreaseMonth, increaseMonth, changeYear, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
				const currentYear = DateTime.fromJSDate(date).year
				const years = Array.from({ length: 15 }, (_, i) => currentYear - 7 + i)

				return (
					<div className="dp-custom-header">
						<button type="button" className="dp-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
							‹
						</button>

						<span className="dp-header-label">
							{DateTime.fromJSDate(date).setLocale(lang).toFormat('MMMM')}{' '}
							<span className="dp-header-year-wrap" ref={yearMenuRef}>
								<button
									type="button"
									className="dp-header-year-trigger"
									aria-haspopup="listbox"
									aria-expanded={yearMenuOpen}
									onClick={() => setYearMenuOpen((open) => !open)}
								>
									<span>{currentYear}</span>
									<span className="dp-header-year-caret" aria-hidden="true">
										▼
									</span>
								</button>

								{yearMenuOpen && (
									<div className="dp-year-menu" role="listbox" aria-label="Select year">
										{years.map((year) => (
											<button
												key={year}
												type="button"
												role="option"
												aria-selected={year === currentYear}
												className={['dp-year-option', year === currentYear ? 'dp-year-option--selected' : '']
													.filter(Boolean)
													.join(' ')}
												onClick={() => {
													changeYear(year)
													setYearMenuOpen(false)
												}}
											>
												{year}
											</button>
										))}
									</div>
								)}
							</span>
						</span>

						<button type="button" className="dp-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
							›
						</button>
					</div>
				)
			},
			[lang, yearMenuOpen],
		)

		const dayClassName = useCallback(
			(date) => {
				const classes = []
				const iso = toIsoDateKey(date)
				if (iso && holidayByIso.has(iso)) classes.push('holiday')

				return classes.length > 0 ? classes.join(' ') : undefined
			},
			[holidayByIso],
		)

		const renderDayContents = useCallback(
			(day, date) => {
				if (!date) return day
				const iso = toIsoDateKey(date)
				const title = iso ? holidayTooltipByIso.get(iso) : undefined
				if (!title) return day

				return (
					<Tooltip title={title}>
						<span>{day}</span>
					</Tooltip>
				)
			},
			[holidayTooltipByIso],
		)

		return (
			<StyledDatePickerWrapper>
				<DatePicker
					ref={ref}
					id={id}
					name={name}
					selected={toDate(value)}
					onChange={handleDateChange}
					disabled={disabled}
					minDate={toDate(minDate)}
					maxDate={toDate(maxDate)}
					dateFormat={localeConfig.format}
					locale={lang}
					dayClassName={dayClassName}
					renderDayContents={renderDayContents}
					renderCustomHeader={renderCustomHeader}
					yearDropdownItemNumber={15}
					scrollableYearDropdown
					calendarStartDay={localeConfig.weekStartsOn ?? 1}
					customInput={
						<DateFieldInput
							id={id}
							name={name}
							label={label}
							required={required}
							disabled={disabled}
							error={error}
							onDateChange={handleDateChange}
							localeConfig={localeConfig}
						/>
					}
					{...rest}
				/>
			</StyledDatePickerWrapper>
		)
	},
)

DatePickerMasked.displayName = 'DatePickerMasked'

/** Generic datepicker component. If `control` and `name` are provided, this uses RHF Controller */
const DefaultDatePicker = ({
	control,
	name,
	rules,
	id,
	label,
	required,
	value,
	onChange,
	disabled,
	error,
	minDate,
	maxDate,
	...rest
}) => {
	if (control && name) {
		return (
			<Controller
				name={name}
				control={control}
				rules={rules}
				render={({ field: { value, onChange, disabled: controllerDisabled }, fieldState: { error: fieldError } }) => (
					<DatePickerMasked
						id={id}
						name={name}
						label={label}
						required={required}
						value={value}
						onChange={onChange}
						disabled={controllerDisabled || disabled}
						error={fieldError?.message ?? error}
						minDate={minDate}
						maxDate={maxDate}
						{...rest}
					/>
				)}
			/>
		)
	}

	return (
		<DatePickerMasked
			id={id}
			name={name}
			label={label}
			required={required}
			value={value}
			onChange={onChange}
			disabled={disabled}
			error={error}
			minDate={minDate}
			maxDate={maxDate}
			{...rest}
		/>
	)
}

export default DefaultDatePicker
